/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { set, action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { WORKFLOW_ITEMS, GROUPED_ITEMS, ActionTypeItem } from 'cio/utils/action-info';
import CampaignModel, { RESTRICTED_ACTION_GROUPS, RESTRICTED_ACTION_TYPES } from 'cio/models/campaign';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import FeaturesService from 'ember-feature-flags/services/features';
import AuthService from 'cio/services/auth';
import { tracked } from '@glimmer/tracking';
import AccountService from 'cio/services/account';
import { AddActionsPanelItemConfig } from 'cio/utils/visual-workflow-builder/add-actions-panel';
import { ActionType } from 'cio/models/action';
import { sortByCaseInsensitive } from 'cio/utils/sort';

interface AddActionsPanelBuildArgs {
  isSaving: boolean;
  campaign: CampaignModel;
  itemConfigs?: Partial<Record<ActionType, AddActionsPanelItemConfig>>;
  hasReadOnlyPermission: boolean;
}

export default class AddActionsPanelBuild extends Component<AddActionsPanelBuildArgs> {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service auth!: AuthService;

  // Internal state
  itemGroups = GROUPED_ITEMS.map((group) => {
    return {
      ...group,
      actionItems: [
        ...group.actionItems.map((item) => {
          if (['webhook_action', 'batch_update_action', 'create_event_action'].includes(item.type)) {
            return {
              ...item,
              enabled: !this.auth.redacted,
            };
          }
          return item;
        }),
      ],
    };
  });
  @tracked isShowingDataCampaignsFeedbackModal = false;

  constructor(owner: unknown, args: AddActionsPanelBuildArgs) {
    super(owner, args);

    this.checkEnabledActionTypes();
    this.removeRestrictedActionGroups();
    this.removeDisabledOrRestrictedActionTypes();
    this.sortMessageGroupItems();
  }

  private checkEnabledActionTypes() {
    WORKFLOW_ITEMS.forEach((i) => set(i, 'enabled', this.isEnabledAction(i)));
  }

  private isEnabledAction(i: ActionTypeItem): boolean {
    return this.environment.enabledAction(i.type) || (i.info.enableByDefault ?? false);
  }

  private removeRestrictedActionGroups() {
    let restrictedGroups = (RESTRICTED_ACTION_GROUPS as any)[this.args.campaign.type];
    if (!isEmpty(restrictedGroups)) {
      this.itemGroups = this.itemGroups.reject((group) => restrictedGroups.includes(group.value));
    }
  }

  private removeDisabledOrRestrictedActionTypes() {
    let restrictedTypes = RESTRICTED_ACTION_TYPES[this.args.campaign.type] ?? [];

    this.itemGroups = this.itemGroups.map((group) => {
      let actionItems = group.actionItems.filter((item) => {
        return !((item.info.hideIfDisabled as boolean) && !item.enabled) && !restrictedTypes.includes(item.type);
      });
      return {
        ...group,
        actionItems,
      };
    });
  }

  private sortMessageGroupItems() {
    this.itemGroups = this.itemGroups.map((group) => {
      if (group.value !== 'messages') {
        return group;
      }
      return {
        ...group,
        actionItems: sortByCaseInsensitive(group.actionItems as ActionTypeItem[], ['label']),
      };
    });
  }

  @action
  onDragStartActionItem(item: ActionTypeItem) {
    this.analytics.track('campaign', 'workflow_new_item_add_started', {
      model: this.args.campaign,
      action: item,
      aggressivePositivityModeEnabled: this.auth.user?.userFlags.aggressive_positivity_mode_on ?? false,
      addAggressivePositivityModeInfo: this.auth.user?.userFlags.aggressive_positivity_available ?? false,
    });
  }
}
